import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

/**
 * Fetch something that changes while you're looking at it, and keep it honest.
 *
 * Call figures go stale the moment they render — a call placed a minute after
 * page load simply won't be there, and a number with no "as at" is a number
 * you can't trust. That's not hypothetical: it's exactly why the Overview
 * disagreed with the PBX about the outbound count.
 *
 * So: refresh on an interval, refresh when the tab is brought back to the
 * front, and always report when the data was last known good.
 *
 * Refreshes are silent — `data` keeps the previous value while the next one is
 * in flight, so the page doesn't flash back to a loading state every minute.
 */
export function useLiveData(path, { intervalMs = 60000, enabled = true } = {}) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null, updatedAt: null });
  const abortRef = useRef(null);
  const inFlightRef = useRef(false);
  const lastLoadRef = useRef(0);

  const load = useCallback(
    ({ silent = false, force = false } = {}) => {
      // One request at a time. A refresh arriving while another is still
      // running — a re-focus during a slow fetch — would only abort the first
      // and start again, which on a single-worker backend stampedes.
      if (inFlightRef.current && !force) return undefined;

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      inFlightRef.current = true;
      lastLoadRef.current = Date.now();

      if (!silent) setState((s) => ({ ...s, status: 'loading' }));

      return api
        .get(path, { signal: ac.signal })
        .then((data) => setState({ status: 'ready', data, error: null, updatedAt: new Date() }))
        .catch((err) => {
          if (err.name === 'AbortError') return;
          // A failed refresh must not blank out good data — show what we have
          // and let the timestamp reveal that it's ageing.
          setState((s) => (s.data
            ? { ...s, status: 'ready', error: err }
            : { status: 'error', data: null, error: err, updatedAt: null }));
        })
        .finally(() => { inFlightRef.current = false; });
    },
    [path],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    load();

    const timer = setInterval(() => {
      // Pointless work against a tab nobody's looking at — and it'd refresh
      // the instant they return anyway.
      if (document.visibilityState === 'visible') load({ silent: true });
    }, intervalMs);

    const onVisible = () => {
      // Re-fetch on re-focus, but not if we just did: coming back to a page
      // that's seconds old shouldn't trigger a fetch, and some environments
      // flip visibility rapidly.
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastLoadRef.current < Math.min(intervalMs, 10000)) return;
      load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      abortRef.current?.abort();
    };
  }, [load, intervalMs, enabled]);

  return { ...state, refresh: () => load({ silent: true, force: true }) };
}
